import { BehaviorSubject, defer, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, pluck, tap, withLatestFrom } from 'rxjs/operators';
import { JSXElementConstructor } from 'react';
import { Store } from 'redux';
import { DocumentNode } from 'graphql';

import { UrlQueryResult } from '../types';

import { convertDataToResolved, FetchedData, fetchFromKnownOrNetwork } from './fetchFromKnownOrNetwork';
import { getKnownRoute, RouteData } from './getKnownRoute';
import { createDebug } from './runtimeDebug';

const debug = createDebug(`resolve`);

export const NOTFOUND_CMP = 'NotFound';
export const ERROR_CMP = 'Error';
export const ERROR_TYPE = 'ERROR';
export const OUTDATED_TYPE = 'OUTDATED';

export interface RootComponentProps {
    id: number | null;
    pathname: string;
}

export interface ResolvedUrl {
    urlKey: string;
    componentName: string;
    id: null | number;
    redirectStatusCode?: number;
    redirectUrl?: string;
}

export interface ResolvedComponent extends ResolvedUrl {
    Cmp: JSXElementConstructor<RootComponentProps | any> | null;
}

export type ResolveFn = (
    pathname: string,
    online$: Observable<boolean>,
    outdated$: Observable<boolean>,
) => Observable<ResolvedComponent>;

export type ResolveParams = {
    components: {
        OfflineComponent: JSXElementConstructor<any>;
        ErrorComponent: JSXElementConstructor<any>;
        OutdatedComponent: JSXElementConstructor<any>;
        NotFoundComponent: JSXElementConstructor<any>;
    };
    asyncLoader(componentName: string): Promise<any> | Observable<any>;
    knownRoutes: RouteData[];
    urlQuery: DocumentNode | string;
    resolveFromNetwork: (urlKey: string) => Observable<FetchedData>;
};

/**
 * @param params
 */
export function resolve(params: ResolveParams) {
    /**
     * Using the current pathname, try to resolve into a known component
     *
     * @param pathname
     * @param outdated$
     */
    function createInputStream(pathname: string, outdated$: Observable<boolean>): Observable<FetchedData> {
        return of(pathname).pipe(
            tap(() => debug('props->pathname +++', pathname)),
            withLatestFrom(outdated$),
            mergeMap(([urlKey, outdated]: [string, boolean]) => {
                if (outdated) {
                    debug('the current codebase is outdated');
                    return of(fetchedDataStub(urlKey, OUTDATED_TYPE));
                }
                return fetchFromKnownOrNetwork(urlKey, params.knownRoutes, params.resolveFromNetwork);
            }),
            catchError((e) => {
                debug('fetchFromKnownOrNetwork gave an error', e);
                return of(fetchedDataStub(pathname, ERROR_TYPE));
            }),
        );
    }

    /**
     *
     * @param componentName
     * @param online
     */
    function getComponent(componentName: string, online: boolean): Observable<JSXElementConstructor<any>> {
        // debugger;
        switch (componentName) {
            case ERROR_CMP: {
                if (online) {
                    debug('ERROR_CMP + online');
                    return of(params.components.ErrorComponent);
                }
                if (!online) {
                    debug('ERROR_CMP + offline');
                    return of(params.components.OfflineComponent);
                }
                return EMPTY;
            }
            case NOTFOUND_CMP: {
                return of(params.components.NotFoundComponent);
            }
            default: {
                return defer(() => params.asyncLoader(componentName)).pipe(
                    map((mod) => (mod as any).default),
                    catchError((e) => {
                        console.error('failed to load a chunk', e);
                        return of(params.components.ErrorComponent);
                    }),
                );
            }
        }
    }

    /**
     * This is the function that gets used
     */
    return function resolveInner(
        pathname: string,
        online$: Observable<boolean>,
        outdated$: Observable<boolean>,
    ): Observable<ResolvedComponent> {
        /**
         * A stream of FetchedData events
         */
        const input$ = createInputStream(pathname, outdated$);

        return input$.pipe(
            /**
             * Convert the gql Response into a Component Name
             */
            mergeMap((resp) => convertDataToResolved(resp)),
            tap((resp) => debug(resp)),
            withLatestFrom(online$),
            mergeMap(([resolved, online]: [ResolvedUrl, boolean]) => {
                return getComponent(resolved.componentName, online).pipe(
                    map((component) => {
                        return {
                            ...resolved,
                            Cmp: component,
                        };
                    }),
                );
            }),
            catchError((e) => {
                console.error('an unknown error during resolve', e);
                return EMPTY;
            }),
        );
    };
}

export type ResolveWeakParams = {
    knownRoutes: RouteData[];
    resolveWeakLoader: (componentName: string) => any;
    urlKey: string;
    syncReadFromClient(urlKey: string): UrlQueryResult | undefined;
};

export function resolveWeak(params: ResolveWeakParams): ResolvedComponent {
    const { urlKey, resolveWeakLoader, knownRoutes, syncReadFromClient } = params;

    /**
     * Attempt to match a 'known' route, meaning no further
     * work or requests are needed
     */
    const knownData = getKnownRoute(urlKey, knownRoutes) || syncReadFromClient(urlKey);

    /**
     * Convert the possibly known route to the correct data shape.
     */
    const [resolvedData, error] = syncObs(convertDataToResolved({ data: { urlResolver: knownData || null }, urlKey }));

    /**
     *  If there IS data + no error, try to lookup the module from the bundles
     *  cache.
     */
    if (resolvedData && !error) {
        const resolvedModule = resolveWeakWebpack(resolvedData, resolveWeakLoader);
        if (resolvedModule) {
            return resolvedModule;
        } else {
            debug('data existed, but could not resolve', resolvedData);
        }
    }

    return {
        urlKey: urlKey,
        id: 0,
        Cmp: null,
        componentName: '',
    };
}

declare const __webpack_modules__: any;
declare const __webpack_require__: any;

/**
 * Resolving a module 'weakly' will prevent it being loaded over the
 * network etc.
 *
 * It's just asking webpack if this has been loaded already, or not.
 * @param resolved
 * @param resolveWeakLoader
 */
function resolveWeakWebpack(
    resolved: ResolvedUrl,
    resolveWeakLoader: (componentName: string) => any,
): ResolvedComponent | undefined {
    const { componentName, urlKey, id } = resolved;
    const maybeModule = resolveWeakLoader(componentName);
    if (maybeModule && __webpack_modules__[maybeModule]) {
        const mod = __webpack_require__(maybeModule);
        if (mod) {
            return {
                urlKey,
                componentName,
                id,
                Cmp: mod.default,
            };
        }
    }
    return undefined;
}

function syncObs<T>(obs$: Observable<T>): [T | undefined, Error | undefined] {
    let output: T | undefined = undefined,
        error = undefined;
    obs$.subscribe({
        next: (res) => (output = res),
        error: (e) => {
            error = e;
        },
    });
    return [output, error];
}

export function fetchedDataStub(urlKey: string, type: string): FetchedData {
    return {
        data: {
            urlResolver: {
                type: type,
                id: 0,
                __typename: 'EntityUrl',
            },
        },
        urlKey,
    } as FetchedData;
}

/**
 * Produce
 * @param store
 * @param online
 * @param outdated
 */
export function getRuntimeState<T extends { runtime: { online: boolean; outdated: boolean } }>(
    store: Store<T>,
    online = true,
    outdated = false,
): Observable<{ online: boolean; outdated: boolean }> {
    const output$ = new BehaviorSubject<{ online: boolean; outdated: boolean }>({ online, outdated });
    const store$ = new Observable<T>((obs) => {
        const unlisten = store.subscribe(() => {
            obs.next(store.getState());
        });
        return () => unlisten();
    });
    store$.pipe(pluck('runtime'), tap(output$)).subscribe();
    return output$;
}
